import { ImagePaths } from '../../lib/imageUrls.js';

export const sharepointCopilotInADay = {
    id: 23,
    slug: 'sharepoint-copilot-in-a-day',
    ogImage: '/og/courses/sharepoint-copilot-in-a-day.jpg',
    courseCode: 'SHARECOP-GOV-001',
    title: 'SharePoint Copilot Governance — SAM, RCD and Permission Remediation',
    subtitle: 'The 1-day governance workshop for IT admins operating Copilot on SharePoint — the post-migration hardening step now SharePoint Server 2016/2019 support has ended (14 July 2026)',
    valueProposition: 'Configure your SharePoint environment for Copilot success — covering information architecture, metadata strategy, permission governance, and content quality — deployed 4 to 8 weeks before Copilot activation. Essential hardening for tenants now absorbing content migrated off SharePoint Server 2016/2019, which reached end of support on 14 July 2026.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'EDUC4TE',
    image: ImagePaths.COURSE_3,
    shortDescription: 'SHARECOP-GOV-001 — SharePoint Copilot governance covering SAM reports, RCD, permission remediation, and cleanup for IT admins in Australia.',
    price: {
        aud: 795,
        currency: 'AUD',
        earlyBird: 695
    },
    series: {
        name: 'EDUC4TE SharePoint Copilot Series',
        position: 'Governance (operate)',
        description: 'Three-course modular series preparing SharePoint environments for Microsoft 365 Copilot — Foundation, Governance, and Enterprise Architecture.',
        courses: [
            { code: 'SHARECOP-FND-001', title: 'SharePoint Copilot Readiness — Foundation', slug: 'sharepoint-copilot-readiness-foundation' },
            { code: 'SHARECOP-GOV-001', title: 'SharePoint Copilot Governance — SAM, RCD and Permission Remediation', slug: 'sharepoint-copilot-in-a-day' },
            { code: 'SHARECOP-ARCH-001', title: 'SharePoint Information Architect — Enterprise IA for Copilot', slug: 'sharepoint-information-architect' }
        ],
        bundle: { code: 'SHARECOP-BUNDLE-001', name: '3-Day SharePoint Copilot Series Bundle', aud: 1995, savings: 'Save AUD 390 (~16%) vs individual' },
        contact: 'info@educ4te.com'
    },
    stripeConfig: {
    priceId: 'price_1U3WMmKu1nyc322UBnclDYEo',
    productId: 'prod_V3deeNg5VfZRID'
  },

    snapshot: {
        whoItsFor: [
            'SharePoint Site Owners',
            'Information Architects',
            'IT Administrators',
            'Governance and Compliance teams'
        ],
        accreditation: 'Aligned to Microsoft 365 Copilot deployment guidance and Australian Privacy Act 1988'
    },

    logistics: {
        classSize: 'Maximum 20 participants for hands-on lab work',
        requirements: [
            'SharePoint Site Owner experience or M365 Administrator experience',
            'Access to a Microsoft 365 tenant (trial or production)',
            'SharePoint Online admin permissions for lab exercises',
            'Computer with internet access and a modern browser'
        ]
    },

    overview: 'Microsoft 365 Copilot grounds every response in your SharePoint content — which means poor information architecture, excessive permission inheritance, and inconsistent metadata translate directly into inaccurate, unreliable AI responses. This intensive one-day course prepares SharePoint practitioners to build a Copilot-ready environment before activation. You will map the retrieval-augmented generation pipeline from SharePoint to Copilot responses, design hub site hierarchies and content types that optimise AI discovery, build a managed metadata taxonomy for column-level grounding, audit and remediate oversharing risks across site collections, and establish content governance policies that keep your environment Copilot-accurate over time. Five hands-on labs guide you from assessing your current environment through to deploying a governance framework ready for Day 1 Copilot activation. Australian organisations must also consider Privacy Act 1988 data sovereignty implications and ACSC guidance when deploying Copilot — both are addressed throughout. Participants leave with a completed readiness assessment, an information architecture plan, a metadata taxonomy, a permission remediation report, and a governance policy document.',

    outcomes: [
        'Explain how Microsoft 365 Copilot retrieves and grounds responses using SharePoint content via retrieval-augmented generation',
        'Assess an existing SharePoint environment for Copilot readiness across architecture, metadata, permissions, and content quality',
        'Design hub site hierarchies and content type structures that maximise Copilot accuracy and response relevance',
        'Build a managed metadata taxonomy with column-level grounding to support AI-optimised content discovery',
        'Identify and remediate permission oversharing risks using SharePoint admin tools and Microsoft 365 governance features',
        'Apply ACSC and Privacy Act 1988 guidance to data sovereignty and sensitivity considerations for Copilot-indexed content',
        'Create document naming conventions, retention policies, and archival workflows that maintain Copilot content quality over time',
        'Develop a phased governance plan that keeps a SharePoint environment Copilot-accurate as content and teams evolve'
    ],

    audience: [
        'SharePoint Site Owners managing Copilot-targeted environments',
        'Information Architects designing content structures for AI readiness',
        'IT Administrators responsible for M365 Copilot deployment',
        'Governance and compliance professionals overseeing Copilot rollout',
        'Microsoft 365 administrators with SharePoint responsibilities',
        'Change managers leading Copilot adoption programmes'
    ],

    prerequisites: [
        'SharePoint Site Owner experience or M365 Administrator experience',
        'Familiarity with SharePoint Online sites, libraries, and permissions',
        'Basic understanding of Microsoft 365 admin centre navigation',
        'No prior Copilot or AI experience required'
    ],

    modules: [
        {
            title: 'Copilot Architecture and Requirements',
            duration: '1.5 hours',
            topics: [
                'How M365 Copilot grounds responses in SharePoint content',
                'Retrieval-augmented generation (RAG) fundamentals for practitioners',
                'Permission model and oversharing risks in Copilot context',
                'Information architecture impact on Copilot response accuracy',
                'Lab 1: Assess current SharePoint environment readiness (20 min)'
            ]
        },
        {
            title: 'Information Architecture for AI',
            duration: '2 hours',
            topics: [
                'Site hierarchy and navigation design for Copilot discoverability',
                'Hub site architecture patterns for AI-optimised content grouping',
                'Content type design, inheritance, and lifecycle management',
                'Metadata schema planning aligned to Copilot grounding requirements',
                'Taxonomy and term store management for enterprise-scale AI',
                'Lab 2: Design information architecture strategy for Copilot readiness (30 min)'
            ]
        },
        {
            title: 'Metadata Strategy',
            duration: '1.5 hours',
            topics: [
                'Managed metadata vs enterprise keywords: when to use each',
                'Column-level metadata for AI grounding and content targeting',
                'Automatic vs manual tagging strategies and governance trade-offs',
                'Metadata inheritance patterns across sites, libraries, and content types',
                'Lab 3: Deploy metadata taxonomy for AI-optimised discovery (25 min)'
            ]
        },
        {
            title: 'Permission Audit and Remediation',
            duration: '1.5 hours',
            topics: [
                'Oversharing detection with SharePoint Advanced Management (SAM): data access governance reports, sharing link and EEEU reports, and site access reviews',
                'Interim Copilot protections: Restricted Content Discovery (RCD) and Restricted Access Control, and why Restricted SharePoint Search is retiring in favour of RCD',
                'Breaking permission inheritance safely and managing unique permissions across large site collections',
                'SharePoint Groups vs M365 Groups strategy for Copilot environments',
                'Lab 4: Execute permission audit and create remediation plan (30 min)'
            ]
        },
        {
            title: 'Content Cleanup and Governance',
            duration: '1.5 hours',
            topics: [
                'Content quality assessment frameworks for Copilot-indexed libraries',
                'Stale content and site lifecycle: SAM inactive site and ownership policies, archival, and retention policy design',
                'Document naming conventions that improve AI accuracy and discoverability',
                'Retention policies for Copilot-indexed content under Privacy Act 1988',
                'Lab 5: Build content cleanup workflow and governance policy (25 min)'
            ]
        }
    ],

    format: {
        type: 'Live instructor-led day workshop, intensive online delivery, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
        includes: [
            '8 hours of instructor-led training with live SharePoint tenant access',
            '5 hands-on labs with step-by-step exercise guides',
            'SharePoint Copilot Readiness Assessment template',
            'Information architecture design workbook',
            'Permission audit and remediation checklist',
            'Governance policy framework document',
            'Certificate of completion',
            'Access to course materials and lab guides for 12 months'
        ]
    },

    nextIntake: {
        date: 'Register your interest — we will confirm the next cohort',
        details: 'Full-day instructor-led workshop (9:00 AM to 5:00 PM) or intensive online delivery across two half-days'
    },

    faqs: [
        {
            question: 'How far in advance of Copilot activation should we take this course?',
            answer: 'We recommend completing this course and implementing the lab outputs 4 to 8 weeks before activating Microsoft 365 Copilot. This gives your team time to remediate permissions, deploy metadata taxonomy, and clean up stale content before Copilot indexes your environment. Deploying Copilot without preparation commonly results in inaccurate responses and oversharing incidents.'
        },
        {
            question: 'Do participants need their own SharePoint tenant for the labs?',
            answer: 'Yes, each participant needs access to a Microsoft 365 tenant with SharePoint Online and SharePoint admin permissions. This can be a production tenant (in a test site collection), a developer tenant, or a Microsoft 365 trial. We provide lab guides that work across all three options and advise on setting up a safe lab environment during the pre-course briefing.'
        },
        {
            question: 'Is this course relevant for Australian Privacy Act compliance?',
            answer: 'Yes. Copilot-indexed SharePoint content falls under Privacy Act 1988 obligations, particularly when personal information is involved. The course covers data sovereignty considerations, sensitivity label strategies, and retention policies that align with Australian Privacy Principles and ACSC guidance. We also address the implications of Microsoft 365 Copilot data residency for Australian organisations.'
        },
        {
            question: 'What if our SharePoint environment is already live with Copilot enabled?',
            answer: 'The course is equally valuable for brownfield environments. Modules 3, 4, and 5 focus specifically on auditing and remediating existing environments — covering metadata retrofitting, permission oversharing remediation, and content quality improvement. Many organisations enrol after experiencing poor Copilot accuracy and use this course to correct their foundation.'
        },
        {
            question: 'Can multiple team members from the same organisation enrol together?',
            answer: 'Yes, and we strongly recommend it. SharePoint Copilot readiness spans site owners, IT administrators, and governance teams — aligning all three roles in one workshop avoids conflicting decisions during implementation. For teams of 5 or more, contact us for a private quote. In-house delivery is also available.'
        },
        {
            question: 'Does this course cover Microsoft Purview sensitivity labels and DLP?',
            answer: 'Module 4 introduces sensitivity label strategy as part of the permission and governance framework, particularly how labels interact with Copilot access decisions. Deep configuration of Microsoft Purview and DLP policies is covered in our separate Microsoft 365 Security Hardening course, which pairs well with this course for a complete Copilot governance capability. Agent governance and Essential Eight alignment are covered in our Copilot Wave 3 & Agentic AI and Copilot Security Roadmap: Essential Eight Alignment courses.'
        },
        {
            question: 'Will I receive a recognised credential after completing the course?',
            answer: 'Participants receive an Educ4te Certificate of Completion upon finishing the course and completing the readiness assessment lab. This certificate can be added to your LinkedIn profile and demonstrates practical SharePoint Copilot governance capability to your organisation and clients.'
        },
        {
            question: 'Is in-house delivery available for our organisation?',
            answer: 'Yes. We offer tailored in-house delivery for organisations across Australia, using your live SharePoint tenant and real governance scenarios from your environment. In-house delivery is particularly effective for teams planning an imminent Copilot rollout. Contact us to discuss your requirements and receive a custom quote.'
        },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ]
};
